import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, Form, Button, Badge } from "react-bootstrap";
import Spinner from "../generics/Spinner";

const TASK_TYPES = [
  { value: "CLEANING", label: "Cleaning" },
  { value: "INSPECTION", label: "Inspection" },
  { value: "MAINTENANCE", label: "Maintenance" },
  { value: "SANITIZATION", label: "Sanitization" },
  { value: "SAFETY_CHECK", label: "Safety Check" },
  { value: "OTHER", label: "Other" },
];

const TARGET_TYPES = [
  { value: "MACHINE", label: "Machine" },
  { value: "FACILITY", label: "Facility" },
];

const FREQUENCIES = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "BIWEEKLY", label: "Bi-weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "QUARTERLY", label: "Quarterly" },
  { value: "BIANNUAL", label: "Bi-annual" },
  { value: "ANNUAL", label: "Annual" },
  { value: "CUSTOM", label: "Custom" },
];

const getFrequencyStyle = (frequency) => {
  const styles = {
    DAILY: { backgroundColor: "#0d6efd", color: "#fff" },
    WEEKLY: { backgroundColor: "#6f42c1", color: "#fff" },
    BIWEEKLY: { backgroundColor: "#6610f2", color: "#fff" },
    MONTHLY: { backgroundColor: "#198754", color: "#fff" },
    QUARTERLY: { backgroundColor: "#fd7e14", color: "#fff" },
    BIANNUAL: { backgroundColor: "#ffc107", color: "#000" },
    ANNUAL: { backgroundColor: "#dc3545", color: "#fff" },
    CUSTOM: { backgroundColor: "#6c757d", color: "#fff" },
  };
  return styles[frequency] || styles.MONTHLY;
};

const getTaskTypeStyle = (taskType) => {
  const styles = {
    CLEANING: { backgroundColor: "#20c997", color: "#fff" },
    INSPECTION: { backgroundColor: "#0dcaf0", color: "#000" },
    MAINTENANCE: { backgroundColor: "#6f42c1", color: "#fff" },
    SANITIZATION: { backgroundColor: "#fd7e14", color: "#fff" },
    SAFETY_CHECK: { backgroundColor: "#dc3545", color: "#fff" },
    OTHER: { backgroundColor: "#6c757d", color: "#fff" },
  };
  return styles[taskType] || styles.OTHER;
};

const TaskTemplatesList = ({
  templates,
  loading,
  error,
  onFilter,
  onDelete,
}) => {
  const [filters, setFilters] = useState({
    search: "",
    taskType: "",
    targetType: "",
    frequency: "",
    isActive: "",
  });

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    if (onFilter) {
      onFilter(newFilters);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (onFilter) {
      onFilter(filters);
    }
  };

  const clearFilters = () => {
    const clearedFilters = {
      search: "",
      taskType: "",
      targetType: "",
      frequency: "",
      isActive: "",
    };
    setFilters(clearedFilters);
    if (onFilter) {
      onFilter(clearedFilters);
    }
  };

  const handleDelete = (template) => {
    if (window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      onDelete(template.id);
    }
  };

  if (loading && templates.length === 0) {
    return <Spinner />;
  }

  return (
    <div className="task-templates-list">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          <i className="fa fa-clipboard me-2"></i>
          Task Templates
        </h4>
        <Link to="/maintenance/task-templates/new" className="btn btn-primary btn-sm">
          <i className="fa fa-plus me-1"></i> Add Template
        </Link>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-header reports-filter">
          <Form onSubmit={handleSearch} className="row align-items-center mb-0">
            <div className="col-12 col-sm-3">
              <label className="col-form-label filter-label" htmlFor="templateSearch">
                <i className="fa fa-search mr-05 ml-05"></i>
                Search
              </label>
              <input
                type="text"
                id="templateSearch"
                className="form-control"
                placeholder="Name..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>
            <div className="col-12 col-sm-2">
              <label className="col-form-label filter-label ml-05">
                Task Type
              </label>
              <select
                className="form-control"
                value={filters.taskType}
                onChange={(e) => handleFilterChange("taskType", e.target.value)}
              >
                <option value="">All Types</option>
                {TASK_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="col-form-label filter-label ml-05">
                Target
              </label>
              <select
                className="form-control"
                value={filters.targetType}
                onChange={(e) => handleFilterChange("targetType", e.target.value)}
              >
                <option value="">All Targets</option>
                {TARGET_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="col-form-label filter-label ml-05">
                Frequency
              </label>
              <select
                className="form-control"
                value={filters.frequency}
                onChange={(e) => handleFilterChange("frequency", e.target.value)}
              >
                <option value="">All Frequencies</option>
                {FREQUENCIES.map((freq) => (
                  <option key={freq.value} value={freq.value}>
                    {freq.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-1">
              <label className="col-form-label filter-label ml-05">
                Status
              </label>
              <select
                className="form-control"
                value={filters.isActive}
                onChange={(e) => handleFilterChange("isActive", e.target.value)}
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="col-12 col-sm-2 d-flex align-items-end" style={{ paddingTop: "32px" }}>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearFilters}>
                <i className="fa fa-times me-1"></i> Clear
              </button>
            </div>
          </Form>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger py-2">
          <i className="fa fa-exclamation-circle me-2"></i>
          {error.message || "Error loading task templates"}
        </div>
      )}

      {/* Results */}
      <Card>
        <Card.Body className="p-0">
          {templates.length === 0 ? (
            <div className="shipment-list--empty text-center py-5">
              <i className="fa fa-clipboard fa-3x mb-3 text-muted"></i>
              <p className="mb-0 text-muted">No task templates found</p>
            </div>
          ) : (
            <table className="table table-striped table-bordered datatable mb-0">
              <thead>
                <tr>
                  <th>Template Name</th>
                  <th>Task Type</th>
                  <th>Target</th>
                  <th>Frequency</th>
                  <th>Est. Duration</th>
                  <th>Photo Required</th>
                  <th>Status</th>
                  <th className="text-center" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template.id}>
                    <td>
                      <Link to={`/maintenance/task-templates/${template.id}`}>
                        <strong>{template.name}</strong>
                      </Link>
                      {template.description && (
                        <small
                          className="d-block text-muted text-truncate"
                          style={{ maxWidth: "200px" }}
                        >
                          {template.description}
                        </small>
                      )}
                    </td>
                    <td>
                      <span
                        className="badge badge-pill"
                        style={getTaskTypeStyle(template.task_type)}
                      >
                        {template.task_type_display || template.task_type}
                      </span>
                    </td>
                    <td>
                      <Badge bg={template.target_type === "MACHINE" ? "info" : "secondary"}>
                        {template.target_type_display || template.target_type}
                      </Badge>
                    </td>
                    <td>
                      <span
                        className="badge badge-pill"
                        style={getFrequencyStyle(template.frequency)}
                      >
                        {template.frequency_display || template.frequency}
                      </span>
                    </td>
                    <td>{template.estimated_duration_minutes || "-"} min</td>
                    <td>
                      {template.requires_photo ? (
                        <i className="fa fa-camera text-success"></i>
                      ) : (
                        <i className="fa fa-times text-muted"></i>
                      )}
                    </td>
                    <td>
                      {template.is_active ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="secondary">Inactive</Badge>
                      )}
                    </td>
                    <td className="text-center">
                      <Link
                        to={`/maintenance/task-templates/${template.id}/edit`}
                        className="btn btn-sm btn-outline-primary me-1"
                        title="Edit"
                      >
                        <i className="fa fa-pencil"></i>
                      </Link>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(template)}
                        title="Delete"
                      >
                        <i className="fa fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card.Body>
      </Card>

      {templates.length > 0 && (
        <div className="text-muted small mt-2">
          Showing {templates.length} template{templates.length !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
};

export default TaskTemplatesList;
